import {
    Box,
    Typography,
    IconButton,
    Slider,
    Collapse,
    Tooltip,
} from "@mui/material";
import PhoneIcon from "@mui/icons-material/Phone";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import PauseIcon from "@mui/icons-material/Pause";
import SubjectIcon from "@mui/icons-material/Subject";
import { useState } from "react";
import TranscriptPanel from "@components/virtual-receptionist/calls/recording/components/TranscriptPanel";
import { useAudioPlayer } from "@components/shared/hooks/useAudioPlayer";
import { formatUSPhone } from "../../../../utils";
import { BulkResolvedMap } from "@components/virtual-receptionist/contacts/hooks/useBulkResolveContacts";
import {
    getDisplayName,
    getRoleLabel,
} from "@components/virtual-receptionist/contacts/types/bulk-resolver.types";
import { useContactCenterStyles } from "@components/virtual-receptionist/theming/useContactCenterStyles";

type Props = {
    rec: any;
    bulkResolvedMap?: BulkResolvedMap;
};

const formatDuration = (seconds?: number | null) => {
    if (!seconds || seconds <= 0) return "0:00";

    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);

    return `${mins}:${secs.toString().padStart(2, "0")}`;
};

const formatDateTime = (value?: string | null) => {
    if (!value) return { date: "--", time: "--" };

    const date = new Date(value);

    return {
        date: date.toLocaleDateString(),
        time: date.toLocaleTimeString([], {
            hour: "2-digit",
            minute: "2-digit",
        }),
    };
};

export default function RecordingItem({
    rec,
    bulkResolvedMap = {},
}: Props) {
    const styles = useContactCenterStyles();
    const [transcriptOpen, setTranscriptOpen] = useState(false);

    const audioUrl = rec.recording?.audioUrl ?? null;
    const recordingSid = rec.recording?.sid ?? null;
    const knownDuration = rec.recording?.durationSeconds ?? rec.durationSeconds ?? 0;

    const {
        isPlaying,
        currentTime,
        duration,
        togglePlay,
        seek,
    } = useAudioPlayer(audioUrl ?? "", knownDuration, { preload: "none" });

    const progress = duration ? (currentTime / duration) * 100 : 0;
    const rawPhone = rec.from || rec.to || "";
    const resolved = rec.contact || bulkResolvedMap?.[rawPhone] || null;
    const isResolved = !rec.contact && !!bulkResolvedMap?.[rawPhone];
    const resolvedWithFlag = resolved
        ? (isResolved ? { ...resolved, isResolved: true } : resolved)
        : null;

    const contactName = resolvedWithFlag ? getDisplayName(resolvedWithFlag) : null;
    const labelText = resolvedWithFlag ? getRoleLabel(resolvedWithFlag) : "";

    const primaryLabel = contactName || formatUSPhone(rawPhone) || "Unknown";
    const secondaryLabel = contactName ? formatUSPhone(rawPhone) : null;

    const handleSliderChange = (_: any, value: number | number[]) => {
        if (!duration) return;

        seek(((value as number) / 100) * duration);
    };

    const { date, time } = formatDateTime(rec.createdAt);

    return (
        <Box sx={styles.messageCard}>
            <Box display="flex" gap={2}>
                <Box sx={styles.phoneIconContainer}>
                    <PhoneIcon sx={{ color: "#007AFF", fontSize: 22 }} />
                </Box>

                <Box flex={1}>
                    <Box
                        display="flex"
                        justifyContent="space-between"
                        alignItems="flex-start"
                        mb={1}
                    >
                        <Box>
                            <Box display="flex" alignItems="center" gap={0.75}>
                                <Typography
                                    sx={{
                                        fontWeight: 800,
                                        fontSize: "0.95rem",
                                        color: "var(--fx-surface-text, #0F172A)",
                                    }}
                                >
                                    {primaryLabel}
                                </Typography>

                                {!!labelText && (
                                    <Box
                                        sx={{
                                            px: 0.8,
                                            py: 0.25,
                                            borderRadius: "4px",
                                            fontSize: "10px",
                                            fontWeight: 700,
                                            bgcolor: "action.selected",
                                            color: "text.secondary",
                                            textTransform: "uppercase",
                                            lineHeight: 1,
                                            whiteSpace: "nowrap",
                                        }}
                                    >
                                        {labelText}
                                    </Box>
                                )}
                            </Box>

                            {!!secondaryLabel && (
                                <Typography
                                    sx={{
                                        fontSize: "0.75rem",
                                        color: "text.secondary",
                                        fontWeight: 500,
                                    }}
                                >
                                    {secondaryLabel}
                                </Typography>
                            )}

                            <Typography
                                sx={{
                                    fontSize: "0.75rem",
                                    color: "text.secondary",
                                    fontWeight: 500,
                                }}
                            >
                                {date} • {time}
                            </Typography>
                        </Box>
                    </Box>
                </Box>
            </Box>

            <Box display="flex" alignItems="center" gap={1.5}>
                <IconButton
                    size="small"
                    sx={styles.playIconButton}
                    disabled={!audioUrl}
                    onClick={togglePlay}
                >
                    {isPlaying ? (
                        <PauseIcon sx={{ fontSize: 22 }} />
                    ) : (
                        <PlayArrowIcon sx={{ fontSize: 22 }} />
                    )}
                </IconButton>

                <Box flex={1}>
                    <Slider
                        value={progress}
                        min={0}
                        max={100}
                        onChange={handleSliderChange}
                        disabled={!audioUrl}
                        sx={styles.miniSlider}
                    />

                    <Box display="flex" justifyContent="space-between" mt={0.3}>
                        <Typography sx={styles.timeLabel}>
                            {formatDuration(currentTime)}
                        </Typography>

                        <Typography sx={styles.timeLabel}>
                            {formatDuration(duration)}
                        </Typography>
                    </Box>
                </Box>

                <Tooltip title={recordingSid ? "Transcript" : "Recording not available"}>
                    <span>
                        <IconButton
                            size="small"
                            sx={styles.playIconButton}
                            disabled={!recordingSid}
                            onClick={() => setTranscriptOpen((previous) => !previous)}
                        >
                            <SubjectIcon sx={{ fontSize: 20 }} />
                        </IconButton>
                    </span>
                </Tooltip>
            </Box>

            <Collapse in={transcriptOpen} unmountOnExit>
                <TranscriptPanel
                    uid={rec.uid}
                    open={transcriptOpen}
                    seed={rec.transcription}
                />
            </Collapse>
        </Box>
    );
}
